import { useEffect, useState } from 'react'
import { Link, useParams, useLocation } from 'react-router-dom'
import { colors } from '../styles/tokens'
import { supabase } from '../lib/supabase'

// Single shared navigation component for the Estimation route tree.
//
// Route hierarchy (verified against actual useParams()/useSearchParams()
// usage across all 16 EstimationBreadcrumb callers -- NOT assumed from a
// larger conceptual hierarchy). Confirmed real URL levels:
//   Level 0: ""                                                    (Tender List)
//   Level 1: tender/:tenderId
//            tender/:tenderId/first-principles
//            tender/:tenderId/compare
//            tender/:tenderId/resources          (Resource Dictionary)
//            tender/:tenderId/cost-codes         (Cost Code Dictionary)
//            -- both Dictionary pages use their own "Back to Tender"
//            link, not this component, so neither needs a case below.
//   Level 2: tender/:tenderId/revision/:revisionId
//   Level 3: tender/:tenderId/revision/:revisionId/<anything else>
//            (BOQ, Rate Build-Up, Takeoff, Markup, Indirect/Risk/HO, etc.
//            all live at this SAME url depth -- confirmed from
//            RateBuildUpPage.jsx: boqItemId is a ?item= query param, not
//            a route segment, and First-Principles Workbench is a modal
//            opened from within Rate Build-Up, not a separate route.
//            There is therefore no real Level 4+ URL to link to; showing
//            one would be a dead/misleading breadcrumb entry.)
//
// first-principles and compare carry no revisionId, so the level cannot
// be inferred from "does revisionId exist" alone -- both are handled
// explicitly as Tender-level (Level 1) by matching their exact pathname
// suffix below.
//
// CHANGE (hierarchical breadcrumb): previously rendered only a single
// "back to direct parent" link. Now renders the full clickable chain
// down to (but not including a link for) the current page, e.g.
// "Tenders › Jeddah Test Tower › Revision 1 › BOQ". Each prior level is
// a real, independently clickable route -- not a derived/simulated one.
// The current page's own label is NOT a link (nothing to navigate to).
//
// Explicit override (`to`/`label`): a handful of pages have a Back
// target that is NOT the plain parent-hierarchy link -- it carries
// page-specific workflow semantics (e.g. "Back to Validation Gate",
// "Back to BOQ" with a tab-reactivating query param, "Back to Takeoff").
// These are preserved EXACTLY as before: when `to` is passed, the
// breadcrumb still renders the full hierarchical chain for levels 0-2
// (Tenders / Tender / Revision, which are never page-specific), but the
// FINAL segment uses the override `to`/`label` verbatim instead of the
// generically-derived Level 3 label. This keeps the override's
// page-specific destination intact while still giving the user the
// higher-level jump links the override itself never provided.
export default function EstimationBreadcrumb({ to: toOverride, label: labelOverride } = {}) {
  const { tenderId, revisionId } = useParams()
  const location = useLocation()

  const [tenderName, setTenderName] = useState(null)
  const [revisionLabel, setRevisionLabel] = useState(null)

  useEffect(() => {
    let cancelled = false
    if (tenderId) {
      supabase.from('tender').select('name').eq('id', tenderId).maybeSingle()
        .then(({ data }) => { if (!cancelled) setTenderName(data?.name ?? null) })
    } else {
      setTenderName(null)
    }
    return () => { cancelled = true }
  }, [tenderId])

  useEffect(() => {
    let cancelled = false
    if (revisionId) {
      supabase.from('estimate_revision').select('label, revision_number').eq('id', revisionId).maybeSingle()
        .then(({ data }) => {
          if (cancelled) return
          setRevisionLabel(data?.label || (data?.revision_number != null ? `Revision ${data.revision_number}` : null))
        })
    } else {
      setRevisionLabel(null)
    }
    return () => { cancelled = true }
  }, [revisionId])

  const style = { fontSize: 13, color: colors.textSecondary, textDecoration: 'none' }
  const sepStyle = { fontSize: 13, color: colors.textMuted, margin: '0 6px' }
  const currentStyle = { fontSize: 13, color: colors.textPrimary, fontWeight: 600 }
  const wrapStyle = { display: 'flex', alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }

  const isFirstPrinciplesOrCompare = /\/(first-principles|compare)$/.test(location.pathname)
  const isRevisionRoot = !!revisionId && new RegExp(`/revision/${revisionId}$`).test(location.pathname)

  // Build the chain of PRIOR levels only (never includes the current
  // page/segment, whether that final segment is generically derived or
  // an explicit toOverride). This chain is identical regardless of
  // toOverride -- the override only changes the FINAL (non-link) label,
  // never the prior Tenders/Tender/Revision links a user can jump to.
  const crumbs = [{ label: 'Tenders', to: '/estimation' }]
  if (tenderId && (revisionId && !isFirstPrinciplesOrCompare)) {
    crumbs.push({ label: tenderName || '…', to: `/estimation/tender/${tenderId}` })
    if (!isRevisionRoot) {
      crumbs.push({ label: revisionLabel || '…', to: `/estimation/tender/${tenderId}/revision/${revisionId}` })
    }
  }

  // Current (non-link) segment's label.
  let currentLabel
  if (toOverride) {
    currentLabel = (labelOverride || 'Current').replace(/^\u2190\s*/, '')
  } else if (!tenderId) {
    currentLabel = 'Tenders'
  } else if (isFirstPrinciplesOrCompare) {
    currentLabel = location.pathname.endsWith('/compare') ? 'Compare' : 'First Principles Library'
  } else if (!revisionId) {
    currentLabel = tenderName || '…'
  } else if (isRevisionRoot) {
    currentLabel = revisionLabel || '…'
  } else {
    currentLabel = 'Current'
  }

  return (
    <nav style={wrapStyle} aria-label="Breadcrumb">
      {crumbs.map((c) => (
        <span key={c.to} style={{ display: 'flex', alignItems: 'center' }}>
          <Link to={c.to} style={style}>{c.label}</Link>
          <span style={sepStyle}>&rsaquo;</span>
        </span>
      ))}
      <span style={currentStyle}>{currentLabel}</span>
    </nav>
  )
}
